import { useCampaignsOverviewCRUDActions } from '@mm/state/actions/CampaignsOverviewCRUDActions';
import { useChatbotCRUDActions } from '@mm/state/actions/ChatbotCRUDActions';
import { useEmailAccountCRUDActions } from '@mm/state/actions/EmailAccountCRUDActions';
import { useTwilioCRUDActions } from '@mm/state/actions/TwilioCRUDActions';
import { EnhancedBillingPage } from '@mm/state/interfaces/connected-page';
import { OnboardingSignupStep } from '@mm/state/model/CompanyUsersModel';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { selectEmailAccounts } from '@mm/state/selectors/emailAccountsSelectors';
import {
  activeFacebookPageSelector,
  enhancedBillingPagesSelector,
} from '@mm/state/selectors/facebookPageSelector';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { selectInstagramAccountId } from '@mm/state/selectors/instagramSelectors';
import {
  hasSMSNumberSelector,
  recentDiscardedSelector,
  selectIsSmsSupported,
} from '@mm/state/selectors/twillio';
import { hasConnectedRealFacebookPageSelector } from '@mm/state/selectors/userSelector';
import { StoreState } from '@mm/state/store-interfaces';
import { assertUnreachable } from '@mm/utils';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useConnectPageHandlers } from '../../../../../../../common/hooks/use-connect-page-handlers';
import { useFreeTrialUpgradeModal } from '../../../../../../../common/modals/FreeTrialUpgradeModal/use-free-trial-upgrade-modal';
import { useSetNextOnboardingStep } from '../../../../../../../dashboard/pages/SignupOnboarding/use-set-next-onboarding-step';
import { IG_AUTOMATIONS_SIGNUP_GA_EVENT } from '../../AutomationHome/TemplateIndex/TemplateGoButton';

export const CONNECTABLE_CHANNELS = [
  'email',
  'sms',
  'facebook',
  'instagram',
] as const;
export type ConnectableChannel = typeof CONNECTABLE_CHANNELS[number];

export const useChannelConnection = (
  onChannelConnected: () => void,
  botId?: number,
  isOnboarding?: boolean,
  otherFacebookPage?: EnhancedBillingPage,
) => {
  const isFacebookConnectedOnActivePage = useSelector(
    hasConnectedRealFacebookPageSelector,
  );
  const isFacebookConnected = otherFacebookPage
    ? !!otherFacebookPage.realFacebookPage
    : isFacebookConnectedOnActivePage;
  const isInstagramConnected = !!useSelector(selectInstagramAccountId);
  const hasInstachampCompanyFlag = useSelector(
    combinedFeatureFlagsSelector,
  ).instachamp;
  const hasNativeIntegrationsFeature = useSelector(
    combinedFeatureFlagsSelector,
  ).nativeIntegrations;

  const isSMSSupported = useSelector(selectIsSmsSupported);
  const activeFacebookPage = useSelector(activeFacebookPageSelector);
  const hasSMSNumber = useSelector(hasSMSNumberSelector);
  const isSMSConnected = activeFacebookPage?.smsNumberActivated || hasSMSNumber;
  const isEmailConnected =
    !!useSelector(selectEmailAccounts)?.length ||
    !!activeFacebookPage?.emailActivated;
  const enhancedBillingPages = useSelector(enhancedBillingPagesSelector);
  const facebookPage = otherFacebookPage
    ? otherFacebookPage
    : activeFacebookPage
    ? activeFacebookPage
    : enhancedBillingPages[0];

  const { isFreeTrialPromotionActive } = useFreeTrialUpgradeModal(facebookPage);
  const recentDiscardedNumber = useSelector(recentDiscardedSelector);
  const hasActivateNumberFlag = useSelector(
    combinedFeatureFlagsSelector,
  ).smsActivateNumber;
  const { openInstagramConnectModal, openActivateSMSNumberModal } =
    useModalActions();
  const connectIgOnSuccess = useRef(false);
  const { attachToFacebookPage } = useChatbotCRUDActions();
  const { setNextOnboardingStep } = useSetNextOnboardingStep();
  const { openAudienceExtractorModal } = useModalActions();
  const { openFreeTrialUpgradeModalPromotional } = useFreeTrialUpgradeModal();
  const enabledInputTriggers = useSelector(
    (state: StoreState) => state.campaignsOverview.enabledInputTriggers,
  );
  const isFreePlan =
    !facebookPage?.subscription && !facebookPage?.redemption?.freeTrial;
  const isCanceledPlan =
    facebookPage?.subscription?.canceledAt &&
    facebookPage?.subscription?.state !== 'active';
  const campaignsOverviewCRUDActions = useCampaignsOverviewCRUDActions();
  const retrieveEnableInputTriggers = useCallback(() => {
    if (botId) {
      campaignsOverviewCRUDActions.retrieveEnabledInputTriggers(botId);
    }
  }, [botId, campaignsOverviewCRUDActions]);

  const skipFetchActivePage = otherFacebookPage
    ? otherFacebookPage?.id !== activeFacebookPage?.id
    : false;

  useEffect(() => {
    retrieveEnableInputTriggers();
  }, [retrieveEnableInputTriggers]);

  const onSuccessConnect = useCallback(
    channel => {
      if (
        !enabledInputTriggers.find(
          trigger =>
            trigger ===
            (channel === 'instagram'
              ? 'instagram_catch_all'
              : 'facebook_catch_all'),
        )
      ) {
        if (isOnboarding) {
          onChannelConnected();
        } else if (!hasInstachampCompanyFlag) {
          openAudienceExtractorModal({ channel });
        }
      } else {
        onChannelConnected();
      }
    },
    [
      openAudienceExtractorModal,
      onChannelConnected,
      enabledInputTriggers,
      isOnboarding,
      hasInstachampCompanyFlag,
    ],
  );

  const { connect } = useConnectPageHandlers({
    otherFacebookPage: facebookPage,
    onSuccess: () => {
      if (!isFacebookConnected && connectIgOnSuccess.current) {
        openInstagramConnectModal({
          showCongratulations: true,
          eventName: IG_AUTOMATIONS_SIGNUP_GA_EVENT,
          onSuccessInstagramConnect: () => onSuccessConnect('instagram'),
          otherFacebookPage: facebookPage,
        });
        return;
      }
      onSuccessConnect('facebook');
    },
  });

  const { activatePage } = useTwilioCRUDActions();
  const { getGmailOauthUrl } = useEmailAccountCRUDActions();
  const connectInstagramAccount = useMemo(
    () => async () => {
      if (!isFacebookConnected) {
        connectIgOnSuccess.current = true;
        await connect();
      } else {
        openInstagramConnectModal({
          showCongratulations: true,
          onSuccessInstagramConnect: () => onSuccessConnect('instagram'),
          otherFacebookPage: facebookPage,
        });
      }
    },
    [
      connect,
      isFacebookConnected,
      openInstagramConnectModal,
      onSuccessConnect,
      facebookPage,
    ],
  );
  const onClickActivateNumber = useMemo(
    () => async () => {
      if (recentDiscardedNumber && !isFreePlan && !isCanceledPlan) {
        activatePage(facebookPage.id, true, {
          numberKind: 'twilio',
          number: recentDiscardedNumber,
          skipFetchActivePage,
        });
        return;
      }

      if (!hasActivateNumberFlag) {
        openFreeTrialUpgradeModalPromotional();

        return;
      }

      openActivateSMSNumberModal({
        onSuccessActivateNumber: onChannelConnected,
        otherFacebookPage,
      });
    },
    [
      activatePage,
      facebookPage,
      hasActivateNumberFlag,
      onChannelConnected,
      openActivateSMSNumberModal,
      recentDiscardedNumber,
      isFreeTrialPromotionActive,
      otherFacebookPage,
      skipFetchActivePage,
      isCanceledPlan,
      isFreePlan,
      openFreeTrialUpgradeModalPromotional,
    ],
  );
  const onConnectGmail = useMemo(
    () => async () => {
      if (isOnboarding && botId) {
        await setNextOnboardingStep(OnboardingSignupStep.NONE, false);
      }
      if (!facebookPage?.activeBotId) {
        await attachToFacebookPage(Number(botId), facebookPage.id, true);
      }
      if (!hasNativeIntegrationsFeature && !isFreeTrialPromotionActive) {
        openFreeTrialUpgradeModalPromotional();
        return;
      }
      window.location.href = await getGmailOauthUrl(facebookPage.id);
    },
    [
      getGmailOauthUrl,
      facebookPage,
      setNextOnboardingStep,
      attachToFacebookPage,
      botId,
      isOnboarding,
      hasNativeIntegrationsFeature,
      openFreeTrialUpgradeModalPromotional,
      isFreeTrialPromotionActive,
    ],
  );
  const connectChannel = useMemo(
    () => (channel: ConnectableChannel) => {
      switch (channel) {
        case 'facebook':
          connect();
          break;
        case 'instagram':
          connectInstagramAccount();
          break;
        case 'sms':
          onClickActivateNumber();
          break;
        case 'email':
          onConnectGmail();
          break;
        default:
          assertUnreachable(channel);
      }
    },
    [connect, connectInstagramAccount, onClickActivateNumber, onConnectGmail],
  );

  return {
    connectChannel,
    isFacebookConnected,
    isInstagramConnected,
    isSMSConnected,
    isEmailConnected,
    isSMSSupported,
    recentDiscardedNumber,
  };
};
